// crewbit-admin/src/constants/expenseCategories.ts
//
// CRA T2125 picker source — verbatim mirror of
// crewbit-mobile/data/expenseCategories.ts. 26-value canonical namespace
// shared with `map_category_to_t2125` RPC body. §WorldClass.3 Phase A
// converged this picker to the RPC enum (see mobile file header for the full
// rename + craLine reconciliation table).
//
// Picker is the operator-facing display layer; `map_category_to_t2125` is the
// T2125 line authority (Hard Rule §0.3). Any future drift between picker
// craLine and RPC body is a bug — RPC wins.

export interface ExpenseCategorySubItem {
  value: string;
  label: string;
  craLine: string;
  deductible: boolean;
  defaultPercentage: number;
  notes?: string;
}

export interface ExpenseCategoryGroup {
  group: string;
  label: string;
  icon: string;
  tint: string;
  subcategories: ExpenseCategorySubItem[];
}

export const EXPENSE_CATEGORY_GROUPS: ExpenseCategoryGroup[] = [
  {
    group: 'vehicle', label: 'Vehicle', icon: 'Car', tint: '#0F6CBD',
    subcategories: [
      { value: 'motor_vehicle_fuel', label: 'Fuel & Oil', craLine: '9281', deductible: true, defaultPercentage: 80, notes: 'business-use % applies' },
      { value: 'motor_vehicle_insurance', label: 'Insurance', craLine: '9281', deductible: true, defaultPercentage: 80, notes: 'business-use % applies' },
      { value: 'motor_vehicle_maintenance', label: 'Repairs & Maintenance', craLine: '9281', deductible: true, defaultPercentage: 80, notes: 'business-use % applies' },
      { value: 'mileage', label: 'Mileage (distance-based)', craLine: '9281', deductible: true, defaultPercentage: 100, notes: 'CRA rates: $0.70/km first 5k, $0.64 after' },
    ],
  },
  {
    group: 'travel', label: 'Travel', icon: 'Plane', tint: '#0EA5E9',
    subcategories: [
      { value: 'travel', label: 'Business Travel', craLine: '9200', deductible: true, defaultPercentage: 100, notes: 'flights, hotels, ground transit for business trips' },
    ],
  },
  {
    group: 'office', label: 'Office', icon: 'Building2', tint: '#6366F1',
    subcategories: [
      { value: 'office_supplies', label: 'Supplies', craLine: '8523', deductible: true, defaultPercentage: 100 },
      { value: 'software_subscriptions', label: 'Software & Subscriptions', craLine: '8523', deductible: true, defaultPercentage: 100 },
      { value: 'telephone_internet', label: 'Phone & Internet', craLine: '9220', deductible: true, defaultPercentage: 50, notes: 'business-use % applies' },
    ],
  },
  {
    group: 'supplies', label: 'Tools & Supplies', icon: 'Hammer', tint: '#F97316',
    subcategories: [
      { value: 'tools_small', label: 'Small Tools (<$500)', craLine: '8523', deductible: true, defaultPercentage: 100, notes: 'large tools (>$500) belong in Capital Purchases' },
      { value: 'materials', label: 'Supplies & Materials', craLine: '8811', deductible: true, defaultPercentage: 100 },
      { value: 'uniforms', label: 'Uniforms & Safety Gear', craLine: '8811', deductible: true, defaultPercentage: 100 },
    ],
  },
  {
    group: 'meals', label: 'Meals & Entertainment', icon: 'UtensilsCrossed', tint: '#D97706',
    subcategories: [
      { value: 'meals_entertainment', label: 'Meals & Entertainment', craLine: '8523', deductible: true, defaultPercentage: 50, notes: 'system auto-applies 50% rule' },
    ],
  },
  {
    group: 'professional', label: 'Professional Fees', icon: 'Briefcase', tint: '#8B5CF6',
    subcategories: [
      { value: 'professional_fees', label: 'Accounting, Legal, Consulting', craLine: '8860', deductible: true, defaultPercentage: 100 },
      { value: 'subcontractor', label: 'Subcontractor Payments', craLine: '8360', deductible: true, defaultPercentage: 100, notes: 'T4A required if >$500/year' },
    ],
  },
  {
    group: 'advertising', label: 'Advertising & Marketing', icon: 'Megaphone', tint: '#EC4899',
    subcategories: [
      { value: 'advertising', label: 'Advertising & Marketing', craLine: '8521', deductible: true, defaultPercentage: 100 },
    ],
  },
  {
    group: 'insurance', label: 'Insurance', icon: 'Shield', tint: '#10B981',
    subcategories: [
      { value: 'insurance_business', label: 'Business Insurance', craLine: '8690', deductible: true, defaultPercentage: 100 },
    ],
  },
  {
    group: 'rent', label: 'Rent & Utilities', icon: 'Home', tint: '#14B8A6',
    subcategories: [
      { value: 'rent_business', label: 'Rent / Lease', craLine: '8910', deductible: true, defaultPercentage: 100, notes: 'use Home Office category for home-based %' },
      { value: 'utilities_business', label: 'Utilities', craLine: '8910', deductible: true, defaultPercentage: 100, notes: 'use Home Office category for home-based %' },
    ],
  },
  {
    group: 'home_office', label: 'Home Office', icon: 'House', tint: '#84CC16',
    subcategories: [
      { value: 'home_office', label: 'Home Office Expenses', craLine: '9945', deductible: true, defaultPercentage: 100, notes: 'home office % applies' },
    ],
  },
  {
    group: 'capital', label: 'Capital Purchases', icon: 'PackagePlus', tint: '#A855F7',
    subcategories: [
      { value: 'cca_class', label: 'Capital Asset (>$500, CCA)', craLine: '9936', deductible: true, defaultPercentage: 100, notes: 'subject to CCA depreciation schedule per asset class' },
    ],
  },
  {
    group: 'licensing', label: 'Licensing & Training', icon: 'GraduationCap', tint: '#06B6D4',
    subcategories: [
      { value: 'license_business', label: 'Licensing & Permits', craLine: '8760', deductible: true, defaultPercentage: 100 },
      { value: 'training', label: 'Training & Education', craLine: '8811', deductible: true, defaultPercentage: 100 },
    ],
  },
  {
    group: 'other', label: 'Other', icon: 'MoreHorizontal', tint: '#8E8E93',
    subcategories: [
      { value: 'bank_fees', label: 'Bank & Processing Fees', craLine: '8710', deductible: true, defaultPercentage: 100 },
      { value: 'product_inventory', label: 'Product Inventory (for resale)', craLine: '8300', deductible: true, defaultPercentage: 100, notes: 'cost of goods sold' },
      { value: 'bad_debt_writeoff', label: 'Bad Debt Write-Off', craLine: '8590', deductible: true, defaultPercentage: 100, notes: 'written-off receivables (rare)' },
      { value: 'other', label: 'Other', craLine: '9270', deductible: true, defaultPercentage: 100 },
    ],
  },
];

// ── Flat export (backward compatible) ──

export interface ExpenseCategory {
  value: string;
  label: string;
  craLine: string;
  deductible: boolean;
  defaultPercentage: number;
  notes?: string;
  group: string;
  groupLabel: string;
}

export const EXPENSE_CATEGORIES: ExpenseCategory[] = EXPENSE_CATEGORY_GROUPS.flatMap(g =>
  g.subcategories.map(sub => ({
    value: sub.value, label: sub.label, craLine: sub.craLine,
    deductible: sub.deductible, defaultPercentage: sub.defaultPercentage,
    notes: sub.notes, group: g.group, groupLabel: g.label,
  })),
);

export const EXPENSE_CATEGORY_MAP = Object.fromEntries(
  EXPENSE_CATEGORIES.map(c => [c.value, c]),
);

export const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'debit', label: 'Debit' },
  { value: 'etransfer', label: 'E-Transfer' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'other', label: 'Other' },
];

export function getCategoryLabel(value: string): string {
  return EXPENSE_CATEGORY_MAP[value]?.label ?? value;
}

export function getCategoryGroup(value: string): ExpenseCategoryGroup | undefined {
  return EXPENSE_CATEGORY_GROUPS.find(g => g.subcategories.some(s => s.value === value));
}

export function getCategoryCRALine(value: string): string {
  return EXPENSE_CATEGORY_MAP[value]?.craLine ?? '';
}
